import { Dispatch, SetStateAction } from "react";
import { motion } from "motion/react";
import { useAtomValue } from "jotai";

import { noticeboardDataVar } from "@/variables/globalVar";
import { getText } from "shared/lib";
import { type QuestionData } from "@/components/wizard/lib";
import { animationVariants } from "../lib";

import { useGetObjectsNearAddress } from "shared/apiHooks";
import { useAccount } from "@/hooks";

import { Button, MapComponent, Toggle } from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader";
import { useTreeModuleQuestionTitle } from "./hooks";

export type TreeActivityData = {
  isRemovingTrees?: boolean | undefined;
  removingType?: "trees" | "vegetation" | "both" | undefined;
  isPruningTrees?: boolean | undefined;
  isDiggingTrees?: boolean | undefined;
};

export interface TreeActivityProps {
  onChangeTreeActivityData: Dispatch<SetStateAction<TreeActivityData>>;
  onClickNext: () => void;
  questionData: QuestionData;
  showLoading?: boolean;
  treeActivityData: TreeActivityData;
}

export function TreeActivity({
  onChangeTreeActivityData,
  onClickNext,
  questionData,
  showLoading,
  treeActivityData,
}: TreeActivityProps) {
  const { conversation } = useAccount();
  const noticeboardData = useAtomValue(noticeboardDataVar);

  const shouldShowMap = !!conversation?.address_data?.gps_latitude;

  const questionTitle = useTreeModuleQuestionTitle(conversation, noticeboardData) ||
    getText("Impact to trees and vegetation");

  const { data: nearObjectsResponse } = useGetObjectsNearAddress(
    {
      path: {
        address_id: conversation?.address_data?.id || "",
      },
    },
    {
      enabled: shouldShowMap,
    },
  );

  const renderMap = () => {
    return (
      <MapComponent
        latitude={conversation?.address_data?.gps_latitude ?? undefined}
        longitude={conversation?.address_data?.gps_longitude ?? undefined}
        addressBoundary={conversation?.address_data?.boundary ?? undefined}
        nearObjects={nearObjectsResponse?.data.near_objects ?? undefined}
        className="max-h-[200px] min-h-[200px] w-full rounded bg-[#E3E2E1] md:max-h-[350px] md:min-h-[350px]"
      />
    );
  };

  const renderRemovingTrees = () => {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-textPrimary text-bodylg">
            {getText("Removing any trees or vegetation?")}
          </h3>

          <ul className="flex flex-wrap gap-2">
            <li>
              <Toggle
                autoFocus
                onPressedChange={(pressed) => {
                  if (pressed) {
                    onChangeTreeActivityData((prev) => ({
                      ...prev,
                      isRemovingTrees: true,
                    }));
                  } else {
                    onChangeTreeActivityData((prev) => ({
                      ...prev,
                      isRemovingTrees: undefined,
                    }));
                  }
                }}
                pressed={!!treeActivityData.isRemovingTrees}
                variant="rounded"
              >
                {getText("Yes")}
              </Toggle>
            </li>
            <li>
              <Toggle
                onPressedChange={(pressed) => {
                  if (pressed) {
                    onChangeTreeActivityData((prev) => ({
                      ...prev,
                      isRemovingTrees: false,
                    }));
                  } else {
                    onChangeTreeActivityData((prev) => ({
                      ...prev,
                      isRemovingTrees: undefined,
                    }));
                  }
                }}
                pressed={treeActivityData.isRemovingTrees === false}
                variant="rounded"
              >
                {getText("No")}
              </Toggle>
            </li>
          </ul>
        </div>

        {treeActivityData.isRemovingTrees ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-bodylg text-textPrimary">
              {getText("What are you proposing to remove?")}
            </h3>

            <ul className="flex flex-wrap gap-2">
              <li>
                <Toggle
                  onPressedChange={(pressed) => {
                    if (pressed) {
                      onChangeTreeActivityData((prev) => ({
                        ...prev,
                        removingType: "trees",
                      }));
                    } else {
                      onChangeTreeActivityData((prev) => ({
                        ...prev,
                        removingType: undefined,
                      }));
                    }
                  }}
                  pressed={treeActivityData.removingType === "trees"}
                  variant="rounded"
                >
                  {getText("Trees")}
                </Toggle>
              </li>
              <li>
                <Toggle
                  onPressedChange={(pressed) => {
                    if (pressed) {
                      onChangeTreeActivityData((prev) => ({
                        ...prev,
                        removingType: "vegetation",
                      }));
                    } else {
                      onChangeTreeActivityData((prev) => ({
                        ...prev,
                        removingType: undefined,
                      }));
                    }
                  }}
                  pressed={treeActivityData.removingType === "vegetation"}
                  variant="rounded"
                >
                  {getText("Vegetation")}
                </Toggle>
              </li>
              <li>
                <Toggle
                  onPressedChange={(pressed) => {
                    if (pressed) {
                      onChangeTreeActivityData((prev) => ({
                        ...prev,
                        removingType: "both",
                      }));
                    } else {
                      onChangeTreeActivityData((prev) => ({
                        ...prev,
                        removingType: undefined,
                      }));
                    }
                  }}
                  pressed={treeActivityData.removingType === "both"}
                  variant="rounded"
                >
                  {getText("Both")}
                </Toggle>
              </li>
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPruningTrees = () => {
    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-textPrimary text-bodylg">
          {getText("Pruning trees?")}
        </h3>

        <ul className="flex flex-wrap gap-2">
          <li>
            <Toggle
              onPressedChange={(pressed) => {
                if (pressed) {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isPruningTrees: true,
                  }));
                } else {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isPruningTrees: undefined,
                  }));
                }
              }}
              pressed={!!treeActivityData.isPruningTrees}
              variant="rounded"
            >
              {getText("Yes")}
            </Toggle>
          </li>
          <li>
            <Toggle
              onPressedChange={(pressed) => {
                if (pressed) {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isPruningTrees: false,
                  }));
                } else {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isPruningTrees: undefined,
                  }));
                }
              }}
              pressed={treeActivityData.isPruningTrees === false}
              variant="rounded"
            >
              {getText("No")}
            </Toggle>
          </li>
        </ul>
      </div>
    );
  };

  const renderDiggingTrees = () => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <h3 className="text-textPrimary text-bodylg">
            {getText(
              "Are any of your proposed buildings or works within 15m of a tree?",
            )}
          </h3>

          <p className="text-textSubdued text-bodysm">
            {getText("Including trees beyond the property boundary")}
          </p>
        </div>

        <ul className="flex flex-wrap gap-2">
          <li>
            <Toggle
              onPressedChange={(pressed) => {
                if (pressed) {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isDiggingTrees: true,
                  }));
                } else {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isDiggingTrees: undefined,
                  }));
                }
              }}
              pressed={!!treeActivityData.isDiggingTrees}
              variant="rounded"
            >
              {getText("Yes")}
            </Toggle>
          </li>
          <li>
            <Toggle
              onPressedChange={(pressed) => {
                if (pressed) {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isDiggingTrees: false,
                  }));
                } else {
                  onChangeTreeActivityData((prev) => ({
                    ...prev,
                    isDiggingTrees: undefined,
                  }));
                }
              }}
              pressed={treeActivityData.isDiggingTrees === false}
              variant="rounded"
            >
              {getText("No")}
            </Toggle>
          </li>
        </ul>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end md:mt-8"
      >
        <Button data-testid="next-button" onClick={onClickNext}>
          {getText(questionData.next_button_label || "Next")}
        </Button>
      </motion.div>
    );
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-5 md:w-[600px] md:max-w-[600px]"
      data-testid="step-container-tree-activity"
    >
      {shouldShowMap ? renderMap() : null}

      <div className="flex flex-col gap-1">
        <p className="text-textSubdued text-bodylg">{questionTitle}</p>
      </div>

      <div className="border-interactiveBorder rounded-lg border p-4">
        <div className="flex flex-col gap-3 md:gap-6">
          {renderRemovingTrees()}

          {renderPruningTrees()}

          {renderDiggingTrees()}
        </div>

        {renderFooter()}
      </div>
    </div>
  );
}
